import { useState, useEffect } from 'react';
import { Plus, CreditCard as Edit2, Trash2, X, Building2 } from 'lucide-react';
import { clientService } from '../services/clientService';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useModal } from '../contexts/ModalContext';
import type { Client, ClientInsert } from '../types/client';
import { log } from '../lib/logger';

export function ClientsManager() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { showConfirm, toast } = useModal();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    vat_number: '',
    address: '',
    contact_info: '',
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const data = await clientService.getAll();
      setClients(data);
    } catch (error) {
      log.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await clientService.update(editingClient.id, formData);
      } else {
        const clientData: ClientInsert = {
          ...formData,
          user_id: user?.id,
        };
        await clientService.create(clientData);
      }
      await loadClients();
      handleCloseForm();
    } catch (error) {
      log.error('Error saving client:', error);
    }
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      vat_number: client.vat_number || '',
      address: client.address || '',
      contact_info: client.contact_info || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    const client = clients.find(c => c.id === id);
    if (!client) return;

    try {
      const campaignCount = await clientService.getCampaignCount(client.name);
      if (campaignCount > 0) {
        toast.error(
          t('clients.cannotDeleteWithCampaigns') ||
          `Cannot delete "${client.name}". There ${campaignCount === 1 ? 'is' : 'are'} ${campaignCount} campaign${campaignCount === 1 ? '' : 's'} associated with this client. Please reassign or delete those campaigns first.`
        );
        return;
      }
    } catch (error) {
      log.error('Error checking campaigns:', error);
      toast.error(t('clients.errorCheckingCampaigns') || 'Error checking for associated campaigns');
      return;
    }

    const confirmed = await showConfirm({
      title: t('clients.deleteTitle') || 'Delete Client?',
      message: t('clients.deleteMessage', { name: client.name }) || `Are you sure you want to delete ${client.name}? This action cannot be undone.`,
      confirmText: t('clients.deleteConfirm') || 'Delete Client',
      cancelText: t('common.cancel') || 'Cancel',
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      await clientService.delete(id);
      toast.success(t('clients.deleteSuccess') || 'Client deleted successfully');
      await loadClients();
    } catch (error) {
      log.error('Error deleting client:', error);
      toast.error(t('clients.deleteFailed') || 'Failed to delete client');
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingClient(null);
    setFormData({ name: '', vat_number: '', address: '', contact_info: '' });
  };

  return (
    <>
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('clients.title')}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {t('clients.description')}
            </p>
          </div>
          <div className="text-sm text-gray-600">
            {clients.length} {clients.length === 1 ? t('clients.client') : t('clients.clients')}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100">
        <div className="p-6 border-b">
          <div className="flex items-center justify-end">
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t('clients.addNew')}
            </button>
          </div>
        </div>

      <div className="p-6">
        {loading ? (
          <p className="text-gray-500 text-center py-8">{t('clients.loadingClients')}</p>
        ) : clients.length === 0 ? (
          <p className="text-gray-500 text-center py-8">{t('clients.noClientsYet')}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clients.map((client) => (
              <div key={client.id} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 text-lg">{client.name}</h3>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleEdit(client)}
                      className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-full transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(client.id)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  {client.vat_number && (
                    <div>
                      <span className="font-medium text-gray-600">{t('clients.vat')}</span>
                      <span className="ml-2 text-gray-900">{client.vat_number}</span>
                    </div>
                  )}
                  {client.address && (
                    <div>
                      <span className="font-medium text-gray-600">{t('clients.addressLabel')}</span>
                      <p className="text-gray-900 mt-1">{client.address}</p>
                    </div>
                  )}
                  {client.contact_info && (
                    <div>
                      <span className="font-medium text-gray-600">{t('clients.contactLabel')}</span>
                      <p className="text-gray-900 mt-1">{client.contact_info}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl shadow-2xl ring-1 ring-gray-100 max-w-2xl w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingClient ? t('clients.editClient') : t('clients.addClient')}
              </h2>
              <button onClick={handleCloseForm} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('clients.name')} *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500"
                  placeholder={t('clients.namePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('clients.vatNumber')}</label>
                <input
                  type="text"
                  value={formData.vat_number}
                  onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500"
                  placeholder={t('clients.vatNumberPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('clients.address')}</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500"
                  placeholder={t('clients.addressPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('clients.contactInfo')}</label>
                <textarea
                  value={formData.contact_info}
                  onChange={(e) => setFormData({ ...formData, contact_info: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500"
                  placeholder={t('clients.contactInfoPlaceholder')}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors hover:scale-[1.02]"
                >
                  {editingClient ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </>
  );
}
